import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  KeyRound,
  Laptop,
  Palette,
  Pencil,
  Save,
  UserCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { PageHeader, StatusPill } from "@packages/ui";
import {
  CreditSourceType,
  CreditTransactionType,
  formatCreditAmount,
  type CreditTransactionRow,
  type UserCreditAccountResponse,
  type UserLoginDeviceResponse,
} from "@packages/global";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import ThemeToggle from "../../components/ThemeToggle";
import { useAuthStore } from "../../stores/authStore";
import { profileApi } from "../../services/api";
import { paymentsApi } from "../../services/user";
import { useDialog } from "../../hooks/useDialog";

type SettingsTab = "profile" | "credits" | "security" | "preferences" | "devices";

const tabs: Array<{ key: SettingsTab; label: string }> = [
  { key: "profile", label: "个人资料" },
  { key: "credits", label: "积分流水" },
  { key: "security", label: "账号安全" },
  { key: "preferences", label: "偏好设置" },
  { key: "devices", label: "登录设备" },
];

const ProfilePage: React.FC = () => {
  const { t } = useTranslation("app");
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { alert, toast } = useDialog();
  const [activeTab, setActiveTab] = useState<SettingsTab>("profile");
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
  });
  const [loginDevices, setLoginDevices] = useState<UserLoginDeviceResponse[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [creditAccount, setCreditAccount] = useState<UserCreditAccountResponse | null>(null);
  const [creditTransactions, setCreditTransactions] = useState<CreditTransactionRow[]>([]);
  const [creditsLoading, setCreditsLoading] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setFormData({ name: user?.name || "", email: user?.email || "" });
    }
  }, [isEditing, user?.email, user?.name]);

  useEffect(() => {
    let active = true;
    setDevicesLoading(true);
    profileApi
      .getLoginDevices()
      .then((items) => {
        if (active) setLoginDevices(items);
      })
      .catch(() => {
        if (active) setLoginDevices([]);
      })
      .finally(() => {
        if (active) setDevicesLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (activeTab !== "credits") return;
    let active = true;
    setCreditsLoading(true);
    Promise.all([
      paymentsApi.creditAccount(),
      paymentsApi.creditTransactions({
        query: {
          type: CreditTransactionType.CONSUME,
          sourceType: CreditSourceType.MODEL_CALL,
        },
        pagination: { page: 1, limit: 20, sortBy: "createdAt", sortOrder: "desc" },
      }),
    ])
      .then(([account, transactions]) => {
        if (!active) return;
        setCreditAccount(account);
        setCreditTransactions(transactions.items);
      })
      .catch(() => {
        if (!active) return;
        setCreditAccount(null);
        setCreditTransactions([]);
      })
      .finally(() => {
        if (active) setCreditsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [activeTab]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await profileApi.updateProfile(formData);
      await useAuthStore.getState().getCurrentUser();
      setIsEditing(false);
      toast(t("profile.updateSuccess"), "success");
    } catch (error: any) {
      await alert(
        error.response?.data?.message || t("profile.updateFailed"),
        t("profile.title"),
        "danger",
      );
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-[8px] border border-border-default bg-bg-surface px-3 py-[9px] text-[12.5px] text-text-primary outline-none transition-colors placeholder:text-text-muted focus:border-action-primary focus:ring-2 focus:ring-action-primary-border disabled:bg-bg-surface disabled:text-text-secondary";
  const labelCls = "mb-[6px] block font-mono text-[10.5px] text-text-muted";

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        eyebrow="My Workspace"
        title="用户设置"
        description="管理个人资料、账号安全与使用偏好。"
      />

      <div className="-mx-4 bg-bg-page px-4 sm:-mx-6 sm:px-6">
        <div className="space-y-4">
          <nav className="flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`inline-flex items-center rounded-full border px-[13px] py-[6px] text-[12px] transition-colors ${
                  activeTab === tab.key
                    ? "border-action-primary bg-action-primary font-semibold text-white"
                    : "border-border-default bg-bg-surface font-medium text-text-secondary hover:border-action-primary-border hover:bg-bg-elevated"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          <main className="min-w-0">
          {activeTab === "profile" && (
            <section className="rounded-[12px] border border-border-subtle bg-bg-surface">
              <div className="flex flex-col gap-4 p-5 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex min-w-0 items-center gap-4">
                  <div className="grid h-[64px] w-[64px] shrink-0 place-items-center rounded-[18px] bg-primary-soft text-[24px] font-semibold text-primary-ink">
                    <UserCircle className="h-9 w-9" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="truncate text-[16px] font-semibold text-text-primary">
                      {user?.name || t("profile.name")}
                    </h2>
                    <p className="mt-1 truncate font-mono text-[11px] text-text-muted">
                      {user?.email || "-"}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill tone="success">账号已登录</StatusPill>
                  <StatusPill tone="neutral">{user?.role || t("profile.role")}</StatusPill>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5 border-t border-border-subtle p-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className={labelCls}>{t("profile.name")}</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        className={`${inputCls} pr-9`}
                        disabled={!isEditing || loading}
                        required
                      />
                      <Pencil className="pointer-events-none absolute right-3 top-1/2 h-3 w-3 -translate-y-1/2 text-text-muted" />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>{t("profile.email")}</label>
                    <div className="relative">
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        className={`${inputCls} pr-9`}
                        disabled={!isEditing || loading}
                        required
                      />
                      <Pencil className="pointer-events-none absolute right-3 top-1/2 h-3 w-3 -translate-y-1/2 text-text-muted" />
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 border-t border-border-subtle pt-4">
                  {isEditing ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setIsEditing(false)}
                        className="inline-flex min-h-9 items-center rounded-[8px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-elevated"
                      >
                        {t("profile.cancel")}
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="inline-flex min-h-9 items-center gap-2 rounded-[8px] bg-action-primary px-4 text-[13px] font-semibold text-white hover:bg-action-primary-hover disabled:opacity-50"
                      >
                        <Save className="h-3.5 w-3.5" />
                        {loading ? t("profile.saving") : t("profile.save")}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIsEditing(true)}
                      className="inline-flex min-h-9 items-center gap-2 rounded-[8px] bg-action-primary px-4 text-[13px] font-semibold text-white hover:bg-action-primary-hover"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      {t("profile.edit")}
                    </button>
                  )}
                  <p className="ml-auto text-[11px] text-text-muted">
                    密码修改、登录设备在对应标签页维护
                  </p>
                </div>
              </form>
            </section>
          )}

          {activeTab === "security" && (
            <section className="rounded-[12px] border border-border-subtle bg-bg-surface">
              <div className="border-b border-border-subtle p-5">
                <h2 className="text-[15px] font-semibold text-text-primary">账号安全</h2>
                <p className="mt-1 text-[12.5px] text-text-muted">
                  当前版本已接入密码修改；后续可在同一区域扩展 MFA、登录风险和会话管理。
                </p>
              </div>
              <div className="p-5">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[12px] bg-success-soft text-success">
                      <KeyRound className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-[13.5px] font-semibold text-text-primary">
                        {t("profile.changePassword")}
                      </h3>
                      <p className="mt-1 text-[12.5px] text-text-muted">
                        {t("profile.changePasswordDesc")}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate("/change-password")}
                    className="inline-flex min-h-9 items-center gap-2 rounded-[8px] bg-action-primary px-4 text-[13px] font-semibold text-white hover:bg-action-primary-hover"
                  >
                    <KeyRound className="h-4 w-4" />
                    {t("profile.changePassword")}
                  </button>
                </div>
              </div>
            </section>
          )}

          {activeTab === "credits" && (
            <section className="rounded-[12px] border border-border-subtle bg-bg-surface">
              <div className="flex flex-col gap-4 border-b border-border-subtle p-5 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-[15px] font-semibold text-text-primary">积分与模型消费</h2>
                  <p className="mt-1 text-[12.5px] text-text-muted">
                    查看当前余额和最近 20 条个人 API 模型调用扣费记录。
                  </p>
                </div>
              </div>
              <div className="grid gap-4 p-5 md:grid-cols-4">
                <CreditMetric label="当前余额" value={creditAccount?.balance} tone="primary" />
                <CreditMetric label="累计充值" value={creditAccount?.totalRecharged} tone="success" />
                <CreditMetric label="累计消费" value={creditAccount?.totalConsumed} tone="warning" />
                <CreditMetric label="累计退回" value={creditAccount?.totalRefunded} tone="neutral" />
              </div>
              <div className="border-t border-border-subtle p-5">
                {creditsLoading ? (
                  <div className="rounded-[12px] border border-border-subtle bg-bg-elevated p-4 text-[12.5px] text-text-muted">
                    正在加载积分流水...
                  </div>
                ) : creditTransactions.length === 0 ? (
                  <div className="rounded-[12px] border border-border-subtle bg-bg-elevated p-4 text-[12.5px] text-text-muted">
                    暂无个人 API 模型调用消费记录。
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-[12px] border border-border-subtle">
                    <div className="min-w-[560px]">
                      <div className="grid grid-cols-[1fr_120px_150px] bg-bg-elevated px-4 py-2.5 text-[11px] font-semibold text-text-muted">
                        <div>说明</div>
                        <div>积分</div>
                        <div>时间</div>
                      </div>
                      {creditTransactions.map((item) => (
                        <div key={item.id} className="grid grid-cols-[1fr_120px_150px] border-t border-border-subtle px-4 py-3 text-[12.5px]">
                          <div className="min-w-0 truncate text-text-primary">{item.description}</div>
                          <div className="font-mono text-warning">-{formatCreditAmount(item.amount)}</div>
                          <div className="text-text-muted">{formatDateTime(String(item.createdAt))}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {activeTab === "preferences" && (
            <section className="rounded-[12px] border border-border-subtle bg-bg-surface">
              <div className="border-b border-border-subtle p-5">
                <h2 className="text-[15px] font-semibold text-text-primary">偏好设置</h2>
                <p className="mt-1 text-[12.5px] text-text-muted">
                  个人中心侧栏与全局页面共享同一套主题和语言偏好。
                </p>
              </div>
              <div className="grid gap-4 p-5 md:grid-cols-2">
                <PreferenceCard
                  icon={Palette}
                  title="显示主题"
                  description="在浅色、深色和跟随系统之间切换。"
                >
                  <ThemeToggle />
                </PreferenceCard>
                <PreferenceCard
                  icon={UserCircle}
                  title="界面语言"
                  description="切换中文或英文界面文案。"
                >
                  <LanguageSwitcher />
                </PreferenceCard>
              </div>
            </section>
          )}

          {activeTab === "devices" && (
            <section className="rounded-[12px] border border-border-subtle bg-bg-surface">
              <div className="border-b border-border-subtle p-5">
                <h2 className="text-[15px] font-semibold text-text-primary">登录设备</h2>
                <p className="mt-1 text-[12.5px] text-text-muted">
                  根据最近登录日志识别常用设备，便于核对异常登录来源。
                </p>
              </div>
              <div className="space-y-3 p-5">
                {devicesLoading && (
                  <div className="rounded-[12px] border border-border-subtle bg-bg-elevated p-4 text-[12.5px] text-text-muted">
                    正在加载登录设备...
                  </div>
                )}
                {!devicesLoading && loginDevices.length === 0 && (
                  <div className="rounded-[12px] border border-border-subtle bg-bg-elevated p-4 text-[12.5px] text-text-muted">
                    暂无登录设备记录。重新登录后会自动生成登录日志。
                  </div>
                )}
                {!devicesLoading &&
                  loginDevices.map((device) => (
                    <div
                      key={device.id}
                      className="flex items-start gap-3 rounded-[12px] border border-border-subtle bg-bg-surface p-4"
                    >
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[12px] bg-bg-elevated text-text-secondary">
                        <Laptop className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-[13px] font-semibold text-text-primary">
                            {device.device}
                          </h3>
                          <StatusPill tone="neutral">{device.loginCount} 次登录</StatusPill>
                        </div>
                        <p className="mt-1 text-[12.5px] text-text-secondary">
                          {device.browser} · {device.os}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[10.5px] text-text-muted">
                          <span>IP: {device.ipAddress || "-"}</span>
                          <span>最后登录: {formatDateTime(device.lastLoginAt)}</span>
                        </div>
                        {device.userAgent && (
                          <p className="mt-2 break-words font-mono text-[10.5px] text-text-muted">
                            {device.userAgent}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </section>
          )}
          </main>
        </div>
      </div>
    </div>
  );
};

const PreferenceCard: React.FC<
  React.PropsWithChildren<{
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    description: string;
  }>
> = ({ icon: Icon, title, description, children }) => (
  <div className="rounded-[12px] border border-border-subtle bg-bg-elevated p-4">
    <div className="mb-4 flex items-start gap-3">
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[12px] bg-bg-surface text-primary-ink">
        <Icon className="h-[18px] w-[18px]" />
      </div>
      <div>
        <h3 className="text-[13.5px] font-semibold text-text-primary">{title}</h3>
        <p className="mt-1 text-[12px] leading-5 text-text-muted">{description}</p>
      </div>
    </div>
    {children}
  </div>
);

const CreditMetric: React.FC<{ label: string; value?: number; tone: "primary" | "success" | "warning" | "neutral" }> = ({ label, value, tone }) => {
  const toneClass = {
    primary: "bg-primary-soft text-primary-ink",
    success: "bg-success-soft text-success",
    warning: "bg-warning-soft text-warning",
    neutral: "bg-bg-elevated text-text-secondary",
  }[tone];
  return (
    <div className={`rounded-[12px] border border-border-subtle p-4 ${toneClass}`}>
      <div className="text-[11px] font-medium opacity-80">{label}</div>
      <div className="mt-2 font-mono text-[22px] font-semibold">{formatCreditAmount(value ?? 0)}</div>
    </div>
  );
};

function formatDateTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
}

export default ProfilePage;
